import React, { createContext, useContext, useReducer, useMemo, ReactNode } from 'react';
import { formReducer, initialFormState } from '@context/formReducer';
import { ICurrentAddressInfo } from '@components/TwoYearsAddressHistoryForm/TwoYearsAddressHistoryForm';

import {
  FormState,
  FormActionType,
  ILicensePlateInfoForm,
  IManualLicensePlateInfoForm,
  IEmploymentForm,
  IIncomeForm,
  IVerifyYourIdentity,
  IVerifyYourInformation,
  CDX_ACTIONS,
  IWidgetInitVariables,
} from '../types/form';

interface FormContextType extends FormState {
  updateInitValues: (data: Partial<IWidgetInitVariables>) => void;
  updateLicensePlateInfoFormData: (data: Partial<ILicensePlateInfoForm>) => void;
  updateManualLicensePlateInfoFormData: (data: Partial<IManualLicensePlateInfoForm>) => void;
  updateVerifyYourIdentity: (data: Partial<IVerifyYourIdentity>) => void;
  updateVerifyYourInformationForm: (data: Partial<IVerifyYourInformation>) => void;
  updateEmploymentForm: (data: Partial<IEmploymentForm>) => void;
  updateIncomeForm: (data: Partial<IIncomeForm>) => void;
  setAddressHistory: (data: ICurrentAddressInfo[]) => void;
  setError: (data: string) => void;
  dispatch: React.Dispatch<FormActionType>;
  handleStep: (step: string) => void;
  submitStart: () => void;
  submitSuccess: () => void;
  resetForm: () => void;
}

const FormContext = createContext<FormContextType | undefined>(undefined);

interface StepFormProviderProps {
  children: ReactNode;
}
export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};
export const FormProvider: React.FC<StepFormProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(formReducer, initialFormState);

  const updateInitValues = (data: Partial<IWidgetInitVariables>) => {
    dispatch({ type: CDX_ACTIONS.SET_INIT_VALUES, payload: data });
  };
  const updateLicensePlateInfoFormData = (data: Partial<ILicensePlateInfoForm>) => {
    dispatch({ type: CDX_ACTIONS.SET_LICENSE_PLATE_INFO_FORM, payload: data });
  };

  const updateManualLicensePlateInfoFormData = (data: Partial<IManualLicensePlateInfoForm>) => {
    dispatch({ type: CDX_ACTIONS.SET_MANUAL_LICENSE_PLATE_INFO_FORM, payload: data });
  };

  const updateVerifyYourIdentity = (data: Partial<IVerifyYourIdentity>) => {
    dispatch({ type: CDX_ACTIONS.SET_VERIFY_YOUR_IDENTITY_FORM, payload: data });
  };

  const updateVerifyYourInformationForm = (data: Partial<IVerifyYourInformation>) => {
    dispatch({ type: CDX_ACTIONS.SET_VERIFY_YOUR_INFORMATION_FORM, payload: data });
  };

  const updateEmploymentForm = (data: Partial<IEmploymentForm>) => {
    dispatch({ type: CDX_ACTIONS.SET_EMPLOYMENT_FORM, payload: data });
  };

  const updateIncomeForm = (data: Partial<IIncomeForm>) => {
    dispatch({ type: CDX_ACTIONS.SET_INCOME_FORM, payload: data });
  };

  const setAddressHistory = (data: ICurrentAddressInfo[]) => {
    dispatch({ type: CDX_ACTIONS.SET_ADDRESS_HISTORY, payload: data });
  };

  const setError = (data: string) => {
    dispatch({ type: CDX_ACTIONS.SUBMIT_ERROR, payload: data });
  };

  const handleStep = (step: string) => {
    dispatch({ type: CDX_ACTIONS.SET_STEP, payload: step });
  };

  const submitStart = async () => {
    dispatch({ type: CDX_ACTIONS.SUBMIT_START });
  };
  const submitSuccess = async () => {
    dispatch({ type: CDX_ACTIONS.SUBMIT_SUCCESS });
  };

  const resetForm = () => {
    dispatch({ type: CDX_ACTIONS.RESET_FORM });
  };

  const contextValue = useMemo(
    () => ({
      ...state,
      updateInitValues,
      updateLicensePlateInfoFormData,
      updateManualLicensePlateInfoFormData,
      updateVerifyYourIdentity,
      updateVerifyYourInformationForm,
      updateEmploymentForm,
      updateIncomeForm,
      setAddressHistory,
      submitStart,
      submitSuccess,
      dispatch,
      setError,
      handleStep,
      resetForm,
    }),
    [state]
  );

  return <FormContext.Provider value={contextValue}>{children}</FormContext.Provider>;
};
